import { useState, FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { Mail, Lock, Zap, Shield, Terminal } from 'lucide-react'
import { supabase } from '../services/supabase'

export default function Login() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      })

      if (signInError) {
        setError(signInError.message)
        return
      }
      // Don't navigate - onAuthStateChange will update store, App.tsx will redirect
    } catch (err) {
      setError('An unexpected error occurred')
      console.error('Login error:', err)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-[100dvh] relative flex items-center justify-center bg-[#0a0a12] overflow-y-auto py-6">
      {/* Animated Background */}
      <div className="absolute inset-0">
        {/* Gradient Orbs */}
        <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-cyan-500/10 rounded-full blur-[128px] -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-purple-500/10 rounded-full blur-[128px] translate-x-1/3 translate-y-1/3" />
        <div className="absolute top-1/2 left-1/2 w-[400px] h-[400px] bg-blue-500/5 rounded-full blur-[100px] -translate-x-1/2 -translate-y-1/2" />
        
        {/* Grid Pattern */}
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `
              linear-gradient(rgba(6, 182, 212, 0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(6, 182, 212, 0.1) 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px'
          }}
        />
        
        {/* Scan Line */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div 
            className="absolute w-full h-[2px] bg-gradient-to-r from-transparent via-cyan-400/50 to-transparent animate-pulse"
            style={{ 
              top: '30%',
              animation: 'scan 4s ease-in-out infinite'
            }}
          />
        </div>
      </div>

      {/* Login Card */}
      <div className="relative z-10 w-full max-w-md mx-4">
        <div className="relative backdrop-blur-xl bg-slate-900/80 border border-cyan-500/20 rounded-2xl shadow-2xl shadow-cyan-500/10 overflow-hidden">
          {/* Top Glow Bar */}
          <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-cyan-400 to-transparent" />
          
          <div className="p-5 md:p-8 space-y-5 md:space-y-8">
            {/* Header */}
            <div className="text-center space-y-4">
              {/* Logo */}
              <div className="flex items-center justify-center gap-3">
                <div className="relative">
                  <div className="absolute inset-0 blur-xl bg-cyan-400/60 animate-pulse" />
                  <Zap className="relative w-10 h-10 md:w-12 md:h-12 text-cyan-400" />
                </div>
                <h1 className="text-2xl md:text-4xl font-bold font-display tracking-wider">
                  <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400 bg-clip-text text-transparent">
                    TERRITORY NEXUS
                  </span>
                </h1>
              </div>

              {/* Subtitle */}
              <div className="space-y-2">
                <div className="flex items-center justify-center gap-2 text-cyan-400">
                  <Terminal className="w-4 h-4" />
                  <span className="text-sm font-mono tracking-widest">ACCESS TERMINAL</span>
                </div>
                <p className="text-slate-400 text-sm">
                  Initialize secure connection to the nexus
                </p>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
              {/* Error */}
              {error && (
                <div className="flex items-center gap-3 px-4 py-3 rounded-lg border border-red-500/50 bg-red-500/10">
                  <Shield className="w-5 h-5 text-red-400 flex-shrink-0" />
                  <p className="text-red-400 text-sm">
                    <span className="font-mono text-red-500">ERROR:</span> {error}
                  </p>
                </div>
              )}

              {/* Email */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-300 tracking-wide">
                  Email Address
                </label>
                <div className="relative group">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-cyan-400 transition-colors">
                    <Mail className="w-5 h-5" />
                  </div>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="operator@nexus.terminal"
                    required
                    className="w-full pl-12 pr-4 py-3 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-100 placeholder-slate-500 focus:outline-none focus:border-cyan-500/50 focus:ring-2 focus:ring-cyan-500/20 transition-all"
                  />
                </div>
              </div>

              {/* Password */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-300 tracking-wide">
                  Access Code
                </label>
                <div className="relative group">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-cyan-400 transition-colors">
                    <Lock className="w-5 h-5" />
                  </div>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••••••"
                    required
                    className="w-full pl-12 pr-4 py-3 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-100 placeholder-slate-500 focus:outline-none focus:border-cyan-500/50 focus:ring-2 focus:ring-cyan-500/20 transition-all"
                  />
                </div>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                className="relative w-full py-3 md:py-4 rounded-lg font-display font-semibold tracking-wider text-white overflow-hidden group disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {/* Button Background */}
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-500 transition-all group-hover:opacity-90" />
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                
                {/* Button Glow */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="absolute inset-0 blur-xl bg-cyan-500/30" />
                </div>
                
                {/* Button Content */}
                <span className="relative flex items-center justify-center gap-2">
                  {loading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      <span>AUTHENTICATING...</span>
                    </>
                  ) : (
                    <>
                      <Shield className="w-5 h-5" />
                      <span>ESTABLISH CONNECTION</span>
                    </>
                  )}
                </span>
              </button>

              {/* Register Link */}
              <div className="text-center pt-4 border-t border-slate-700/50">
                <p className="text-slate-400 text-sm">
                  <span className="text-cyan-500/50 font-mono">{'//'} </span>
                  New operator?{' '}
                  <Link
                    to="/register"
                    className="text-cyan-400 hover:text-cyan-300 font-semibold transition-colors hover:underline underline-offset-4"
                  >
                    REQUEST ACCESS
                  </Link>
                </p>
              </div>
            </form>
          </div>

          {/* Bottom Glow Bar */}
          <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-purple-400/50 to-transparent" />
          
          {/* Corner Accents */}
          <div className="absolute top-0 left-0 w-16 h-16 border-l-2 border-t-2 border-cyan-500/30 rounded-tl-2xl" />
          <div className="absolute top-0 right-0 w-16 h-16 border-r-2 border-t-2 border-cyan-500/30 rounded-tr-2xl" />
          <div className="absolute bottom-0 left-0 w-16 h-16 border-l-2 border-b-2 border-purple-500/30 rounded-bl-2xl" />
          <div className="absolute bottom-0 right-0 w-16 h-16 border-r-2 border-b-2 border-purple-500/30 rounded-br-2xl" />
        </div>

        {/* Legal Links & Security Badge */}
        <div className="flex flex-col items-center gap-2 mt-6">
          <div className="flex items-center gap-3 text-slate-500 text-xs">
            <Link to="/privacy" className="hover:text-slate-300 transition-colors">Privacy Policy</Link>
            <span>•</span>
            <Link to="/terms" className="hover:text-slate-300 transition-colors">Terms of Service</Link>
          </div>
          <div className="flex items-center gap-2 text-slate-500 text-xs">
            <Shield className="w-4 h-4" />
            <span className="font-mono">ENCRYPTED CONNECTION • QUANTUM SECURE</span>
          </div>
        </div>
      </div>

      {/* CSS for scan animation */}
      <style>{`
        @keyframes scan {
          0%, 100% { transform: translateY(-100vh); opacity: 0; }
          10%, 90% { opacity: 1; }
          50% { transform: translateY(100vh); }
        }
      `}</style>
    </div>
  )
}
